import {useCallback} from 'react';
import {View, Text, StyleSheet} from 'react-native';
import {useTranslation} from 'react-i18next';
import {AppDataOffer} from '@/data/app-data-interfaces';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {Image} from 'expo-image';
import {Ionicons} from '@expo/vector-icons';
import {BaseCard} from '@/components/common/BaseCard';
import {OfferPriceDisplay} from "@/components/offers/OfferPriceDisplay";
import {getPriceInfo} from "@/lib/utils";

interface OfferCardProps {
    offer: AppDataOffer;
    onPress: (offer: AppDataOffer) => void;
    isPurchased?: boolean;
}

export function OfferCard({offer, onPress, isPurchased}: OfferCardProps) {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const styles = createStyles(theme);

    // Count content types
    const achievementCount = offer.includedAchievements?.length || 0;

    const blurhash = offer.imageBlurhash && offer.imageBlurhash !== 'undefined'
        ? offer.imageBlurhash
        : 'LKO2?U%2Tw=w]~RBVZRi};RPxuwH';

    const handlePress = useCallback(() => {
        onPress(offer);
    }, [offer, onPress]);

    return (
        <BaseCard
            onPress={handlePress}
            activeOpacity={0.7}
            variant="flex"
            style={isPurchased ? styles.cardPurchased : styles.card}
        >
            {/* Offer image on top, full 16:9 */}
            <View style={styles.imageContainer}>
                {offer.image?.md ? (
                    <Image
                        source={{uri: offer.image.md}}
                        placeholder={{blurhash}}
                        style={styles.image}
                        contentFit="cover"
                        transition={200}
                    />
                ) : (
                    <View style={styles.imagePlaceholder}>
                        <Ionicons name="image-outline" size={32} color={theme.colors.secondary}/>
                    </View>
                )}
            </View>

            <View style={styles.content}>
                <Text style={styles.title} numberOfLines={2}>
                    {offer.title}
                </Text>
                <Text style={styles.description} numberOfLines={2}>
                    {offer.description}
                </Text>

                {/* Content badges */}
                <View style={styles.badgesContainer}>
                    {offer.mediaItemCount?.video > 0 && (
                        <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                            <Ionicons name="videocam" size={14} color={theme.colors.secondary} />
                            <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                {t('offers.videoCount', {count: offer.mediaItemCount.video})}
                            </Text>
                        </View>
                    )}
                    {(offer.mediaItemCount?.audio ?? 0) > 0 && (
                        <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                            <Ionicons name="musical-notes" size={14} color={theme.colors.secondary} />
                            <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                {t('offers.audioCount', {count: offer.mediaItemCount!.audio})}
                            </Text>
                        </View>
                    )}
                    {offer.mediaItemCount?.pdf > 0 && (
                        <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                            <Ionicons name="document-text" size={14} color={theme.colors.secondary} />
                            <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                {t('offers.pdfCount', {count: offer.mediaItemCount.pdf})}
                            </Text>
                        </View>
                    )}
                    {(offer.mediaItemCount?.document ?? 0) > 0 && (
                        <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                            <Ionicons name="document-attach" size={14} color={theme.colors.secondary} />
                            <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                {t('offers.documentCount', {count: offer.mediaItemCount!.document})}
                            </Text>
                        </View>
                    )}
                    {(offer.mediaItemCount?.image ?? 0) > 0 && (
                        <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                            <Ionicons name="image" size={14} color={theme.colors.secondary} />
                            <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                {t('offers.imageCount', {count: offer.mediaItemCount!.image})}
                            </Text>
                        </View>
                    )}
                    {achievementCount > 0 && (
                        <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                            <Ionicons name="trophy" size={14} color={theme.colors.secondary} />
                            <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                {t('offers.achievementCount', {count: achievementCount})}
                            </Text>
                        </View>
                    )}
                </View>

                <View style={styles.footer}>
                    <OfferPriceDisplay priceInfo={getPriceInfo(offer)} isPurchased={!!isPurchased} taxBreakdown={offer.taxBreakdown} hideTaxes={true} />
                </View>
            </View>
        </BaseCard>
    );
}

const createStyles = (theme: ThemeType) => StyleSheet.create({
    card: {
        overflow: 'hidden',
    },

    cardPurchased: {
        overflow: 'hidden',
        opacity: 0.7,
    },

    imageContainer: {
        width: '100%',
        aspectRatio: 16 / 9,
        borderTopLeftRadius: theme.borderRadius.md,
        borderTopRightRadius: theme.borderRadius.md,
        overflow: 'hidden',
    },

    image: {
        width: '100%',
        height: '100%',
    },

    imagePlaceholder: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.secondary + '20',
    },

    content: {
        flex: 1,
        padding: theme.spacing.md,
    },

    title: {
        fontSize: theme.fontSize.lg,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.sm,
    },

    description: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
        lineHeight: 20,
        marginBottom: theme.spacing.sm,
    },

    badgesContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.md,
    },

    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.lg,
        gap: theme.spacing.xs,
    },

    badgeText: {
        fontSize: theme.fontSize.xs,
        fontWeight: '500',
    },

    footer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 'auto',
    },
});
